import {NONE_CURRENCY_VALUE} from '@stubhub/currency-utils'
import {
  gcpTree,
  getGeoId,
  getGeoTree,
  getParentGCPId
} from '@stubhub/tracking-utils';
import {toBase64, isDefined} from '@stubhub/general-utils';
import {REGEX_URL, SELL_FLOW_START} from './sell-flow-start-enum';
import {ERROR_ENUM} from './error-enum';

/**
 * Sends a scren_view track with this data:
 *
 * dataLayer.push({
 *  event: "screen_view", // a default GA4 event
 *  "screen_name": "home", // a literal
 *  "store_id": "", // the SH Store ID as string (for compatibility with OB & TB)
 *  "store_country": "", // the two-letter ISO code of the country, such as ES
 *  "visit_platform": "", // one of sh-web, sh-ios, sh-android
 *  "visit_locale": "", // the locale of the current pageview, such as es-ES
 *  "visit_advisory_currency": "", // one of OFF, NONE, EUR, USD, etc.
 *  "visit_guid": "", // the current user's GUID if logged-in, null otherwise
 *});
 *
 * @param {object} track the track object with at least screen_view method
 * @param {object} data the data sent to track
 */
const screenView = (
  track,
  {
    screenName,
    storeId,
    storeCountry,
    locale,
    isAdvisoryCurrencyEnabled,
    advisoryCurrency,
    userGUID
  } = {}
) => {
  track.screen_view({
    screen_name: screenName,
    store_id: storeId?.toString() || null,
    store_country: storeCountry || null,
    visit_platform: 'sh-web',
    visit_locale: locale || null,
    visit_advisory_currency: isAdvisoryCurrencyEnabled
      ? advisoryCurrency || NONE_CURRENCY_VALUE
      : 'OFF',
    visit_guid: userGUID || null
  });
};

/**
 *
 * Sends a begin_publish track with this data:
 *
 * dataLayer.push({
 *  “event”: “begin_publish”, // a custom GA4 event
 *  "sell_flow_start": "", // one of "hub", "event", "relist", "other”
 *  “ticket_mediums_available_qty”: N, // the amount of ticket mediums offered
 *  “ticket_mediums_available”: ”/XXX/YYY/ZZZ/”, // concatenated names as a string
 *  “fulfillment_methods_available_qty”: N, // the amount of fulfillment methods offered
 *  “fulfillment_methods_available”: ”/XXX/YYY/ZZZ/”, // concatenated names as a string
 *});
 *
 * @param {object} track the track object with at least custom method
 * @param {object} data the data sent to track
 */
const beginPublish = (
  track,
  {
    eventId,
    eventName,
    daysToEvent,
    genre,
    inventoryMarket,
    ancestors = {},
    countryCode,
    fulfillmentMethods = [],
    sellFlowStart,
    onSaleDate,
    imageURL,
    eventDateUTC
  }
) => {
  const ticketMediums = getUniqueArray(fulfillmentMethods, 'ticketMedium');
  const fulfillmentNames = getUniqueArray(fulfillmentMethods, 'name');

  track.custom_event({
    event: 'begin_publish',
    sell_flow_start: sellFlowStart || SELL_FLOW_START.OTHER,
    ticket_mediums_available_qty: ticketMediums.length || 0,
    ticket_mediums_available: getTree(ticketMediums),
    fulfillment_methods_available_qty: fulfillmentNames.length || 0,
    fulfillment_methods_available: getTree(fulfillmentNames),
    event_id: eventId?.toString() || null,
    event_name: eventName ? toBase64(eventName) : null,
    days_to_event: isDefined(daysToEvent) ? daysToEvent : null,
    genre: genre || null,
    inventory_market: inventoryMarket || null,
    parent_gcp_id: (ancestors && getParentGCPId(ancestors)?.toString()) || null,
    gcp_tree: gcpTree(ancestors),
    country_code: countryCode || null,
    geo_id: (ancestors && getGeoId(ancestors)?.toString()) || null,
    geo_tree: getGeoTree(ancestors || {}),
    picture_url: imageURL,
    event_date_ts: eventDateUTC ? new Date(eventDateUTC).getTime() / 1000 : null,
    event_onsale_ts: onSaleDate ? new Date(onSaleDate).getTime() / 1000 : null
  });
};

const successPublish = (
  track,
  {
    eventId,
    eventName,
    daysToEvent,
    genre,
    inventoryMarket,
    ancestors = {},
    eventCountry,
    fulfillmentMethods = [],
    medium,
    fulFillmentMethod,
    deliveryNow,
    numTickets,
    connections,
    faceValue,
    ticketPrice,
    ticketCurrency,
    numFeatures,
    numRestrictions,
    sellerType,
    paymenMethod,
    sellFlowStart,
    listingId,
    onSaleDate,
    imageURL,
    eventDateUTC
  }
) => {
  const ticketMediums = getUniqueArray(fulfillmentMethods, 'ticketMedium');
  const fulfillmentNames = getUniqueArray(fulfillmentMethods, 'name');

  track.custom_event({
    event: 'publish',
    // Flow info
    sell_flow_start: sellFlowStart, // one of "hub", "event", "relist", "other'

    // Form info
    ticket_mediums_available_qty: ticketMediums.length || 0,
    ticket_mediums_available: getTree(ticketMediums),
    fulfillment_methods_available_qty: fulfillmentNames.length || 0,
    fulfillment_methods_available: getTree(fulfillmentNames),

    // Event info
    event_id: eventId?.toString() || null,
    event_name: eventName ? toBase64(eventName) : null,
    days_to_event: isDefined(daysToEvent) ? daysToEvent : null,
    genre: genre || null,
    inventory_market: inventoryMarket || null,
    parent_gcp_id: (ancestors && getParentGCPId(ancestors)?.toString()) || null,
    gcp_tree: gcpTree(ancestors),
    country_code: eventCountry || null,
    geo_id: (ancestors && getGeoId(ancestors)?.toString()) || null,
    geo_tree: getGeoTree(ancestors || {}),
    picture_url: imageURL,
    event_date_ts: eventDateUTC ? new Date(eventDateUTC).getTime() / 1000 : null,
    event_onsale_ts: onSaleDate ? new Date(onSaleDate).getTime() / 1000 : null,

    // Listing info
    ticket_medium: medium, // one of 'paper', 'pdf', etc.
    fulfillment: fulFillmentMethod, // one of 'digital', 'shipping', etc.
    tickets_in_hand: deliveryNow, // one of 'ready', 'later'
    ticket_qty: numTickets, // the amount of tickets in the listing
    face_value: faceValue, // in the listing’s own currency, the average if multiple, null if not provided
    price: ticketPrice, // in the listing’s own currency
    currency: ticketCurrency, // the original one for the listing, e.g. 'EUR', 'USD'
    features_qty: numFeatures, // the amount of features & comments (defaults to 0)
    restrictions_qty: numRestrictions, // the amount of restrictions & limitations (defaults to 0)
    connected: connections, // one of 'no' (default), 'employee', 'company', 'organizer'
    listing_id: listingId?.toString() || null, // the listing ID that has been created

    // Seller info
    seller_type: sellerType, //one of 'PRIVATE', 'BUSINESS'
    payout_method: paymenMethod, // one of 'paypal', 'direct_deposit'
    guarantee_cc: null // one of 'existing', 'new'
  });
};

/**
 * Sends a custom event with this data:
 *
 * dataLayer.push({
 *  "event": "sell_add_account_info", // a custom event // seller info
 *  "payout_method", // one of “paypal”, “direct_deposit”
 *  "guarantee_cc", // one of "existing", "new"
 *});
 *
 * @param {object} track the track object with at least screen_view method
 * @param {object} data the data sent to track
 */
const sellAddAccountInfo = (track, {
  payout_method,
  guarantee_cc
}) => {
  track.custom_event({
    event: 'sell_add_account_info',
    payout_method,
    guarantee_cc
  });
};

const getUniqueArray = (array, field) => {
  return Array.from(new Set(array?.map((item) => item[field]?.toLowerCase())));
};

const getTree = (elements = []) => {
  const tree = elements.filter((item) => !!item).join('/');
  if (tree.length) {
    return `/${tree}/`;
  }

  return '/';
};

const sellDenied = (track, {sellFlowStart, eventId, eventName, daysToEvent, genre, inventoryMarket, ancestors, countryCode, onSaleDate, imageURL, eventDateUTC}) => {
  track.custom_event({
    event: 'sell_denied',
    sell_flow_start: sellFlowStart || SELL_FLOW_START.OTHER,
    event_id: eventId?.toString() || null,
    event_name: eventName ? toBase64(eventName) : null,
    days_to_event: isDefined(daysToEvent) ? daysToEvent : null,
    inventory_market: inventoryMarket || null,
    parent_gcp_id: getParentGCPId(ancestors)?.toString() || null,
    gcp_tree: gcpTree(ancestors),
    country_code: countryCode || null,
    geo_id: ancestors && getGeoId(ancestors)?.toString() || null,
    geo_tree: getGeoTree(ancestors || {}),
    genre: genre || null,
    picture_url: imageURL,
    event_date_ts: eventDateUTC ? new Date(eventDateUTC).getTime() / 1000 : null,
    event_onsale_ts: onSaleDate ? new Date(onSaleDate).getTime() / 1000 : null
  });
};

/**
 * Sends a error_user track with this data:
 * dataLayer.push({
 *  "event": "error_user", // a custom event
 *  "code": "", // the error code, usually of the range 400 - 499
 *  "name": “sell_denied”, // a literal
 *  "details": "" // the nature of the error (e.g. “restricted_to_brokers”, etc.)
 * });
*/
const error = (track, {name, error}) => {
  const {code, details} = ERROR_ENUM[error] || ERROR_ENUM.Unknown;
  track.custom_event({
    event: 'error_user',
    code,
    name,
    details
  });
};

/**
 * Sends a sell_add_price track with this data:
 * dataLayer.push({
 *   "event": "sell_add_price", // a custom event  // Listing info - Price (the rest will be taken from the dataLayer)
 *   “face_value”: X.XX, // in the listing’s own currency, the average if multiple, null if not provided
 *   “price”: X.XX, // in the listing’s own currency
 *   “currency”: “”, // the original one for the listing, e.g. “EUR”, “USD”
 * });
 */
const addPrice = (track, {faceValue, price, currency}) => {
  track.custom_event({
    event: 'sell_add_price',
    face_value: faceValue,
    price,
    currency: currency?.toString() || null
  });
};

/**
 * Sends a sell_add_price track with this data:
 * dataLayer.push({
 *   "event": "sell_add_seller_type", // a custom event  // Seller info
 *   “seller_type”: one of “PRIVATE”, “BUSINESS”
 * });
 */
const addSellerType = (track, {sellerType}) => {
  track.custom_event({
    event: 'sell_add_seller_type',
    seller_type: sellerType?.toUpperCase() || null
  });
};

/**
 * Sends a sell_add_fulfillment track with this data:
 * dataLayer.push({
 *   "event": "sell_add_fulfillment", // a custom event  // Listing info - Medium & Fulfillment (the rest will be taken from the dataLayer)
 *   “ticket_medium”: “”, // one of “paper”, “pdf”, etc.
 *   “fulfillment”: “”, // one of “digital”, “shipping”, etc.
 *   “tickets_in_hand”: “”, // one of “ready”, “later”
 *   “ticket_qty”: N, // the amount of tickets in the listing
 * });
 */
const addFulfillment = (track, {ticketMedium, fulfillment, ticketsInHand, ticketQuantity}) => {
  track.custom_event({
    event: 'sell_add_fulfillment',
    ticket_medium: ticketMedium || null,
    fulfillment: fulfillment || null,
    tickets_in_hand: ticketsInHand || null,
    ticket_qty: ticketQuantity || 0
  });
};

/*
  * Sends a sell_add_ticket_details track with this data:
  * dataLayer.push({
  *  "event": "sell_add_ticket_details", // a custom event  // Listing info - Details (the rest will be taken from the dataLayer)
  *  “features_qty”: N, // the amount of features & comments (defaults to 0)
  *  “restrictions_qty”: N, // the amount of restrictions & limitations (defaults to 0)
  *  “connected”: “”, // one of “no” (default), “employee”, “company”, “organizer”
  * });
*/
const addTicketDetails = (track, {featuresQuantity, restrictionsQuantity, connected, limitationsQuantity}) => {
  track.custom_event({
    event: 'sell_add_ticket_details',
    features_qty: featuresQuantity,
    restrictions_qty: restrictionsQuantity,
    limitations_qty: limitationsQuantity,
    connected
  });
};

const chatbot = (track, {storeId, storeCountry, locale, status}) => {
  track.custom_event({
    event: 'use_feature',
    feature_name: `chatbot_${status}`,
    feature_details: null,
    store_id: storeId?.toString() || null,
    store_country: storeCountry || null,
    visit_platform: 'sh-web',
    visit_locale: locale || null,
  });
};

const getSellFlowStart = (referrer) => {
  if (isDefined(referrer) && referrer) {
    if (REGEX_URL.SEARCH.test(referrer)) {
      return SELL_FLOW_START.SEARCH;
    } else if (REGEX_URL.HUB.test(referrer)) {
      return SELL_FLOW_START.HUB;
    } else if (REGEX_URL.EVENT.test(referrer)) {
      return SELL_FLOW_START.EVENT;
    }
  }
  return SELL_FLOW_START.OTHER;
};

const stringToFixedFloat = (value, fixedLength = 2) => {
  if ([null, undefined, ''].includes(value)) {
    return null;
  }

  const parsedValue = Number.parseFloat(value);
  if (Number.isNaN(parsedValue)) {
    return null;
  }

  return Number.parseFloat(parsedValue.toFixed(fixedLength));
};

const getUserGUID = (cookies) => cookies && cookies.get && cookies.get('session_userGUID') || null;

export {
  addFulfillment,
  addPrice,
  addSellerType,
  addTicketDetails,
  beginPublish,
  chatbot,
  error,
  getSellFlowStart,
  getUserGUID,
  screenView,
  sellAddAccountInfo,
  sellDenied,
  stringToFixedFloat,
  successPublish,
};
